import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import { Result, InputNumber, Checkbox, message } from "antd";
import {
  CheckCircleFilled, CheckOutlined, FieldTimeOutlined, InfoCircleFilled, ScheduleOutlined, WarningFilled,
} from "@ant-design/icons";
import { ButtonItem, DatePickerItem, SelectTurnosItem } from "../../../../Components/Items";
import SelectRelojesChecadoresItem from "../../../../Components/SelectRelojesChecadoresItem";
import { claveFechaDia } from "../../../../Components/fmtFechaDia";
import "./AltaEmpleadoPorPasos.css";

// Último paso del alta por pasos, ya con el empleado activo: "¿lo das de alta en un reloj
// checador?". Usa el mismo endpoint que "Emparejar" de InformacionRelojChecador
// (asignacionesRelojes/agregarAsignacionReloj), que ya encola al empleado para el reloj.
//
// Ese endpoint no impide asignar dos veces al mismo empleado en el mismo reloj, así que aquí no
// se deja repetir un reloj ya asignado y el botón se apaga mientras la petición está en curso.
//
// Si se manda a un reloj, también se le asigna su turno, y es OBLIGATORIO (pedido del usuario,
// 2026-10-26): con el mismo endpoint que Agregar Asignación de Turno
// (asignacionesTurnos/agregarAsignacionTurno), inicio = fecha de alta y, salvo que se capture un
// fin, abierta hasta el 31/12/2099 (el mismo centinela del importador de asignaciones). Mientras
// falte, "Terminar" se apaga y se le avisa al asistente (onTurnoPendiente) para que su botón de
// salir tampoco deje irse. Sólo se perdona si la empresa no tiene turnos activos.

const FIN_ABIERTO = "2099-12-31";

// El día elegido como moment local a las 00:00, sin la hora que le pega DatePickerItem al capturar
// con el teclado. Los reportes de asistencia comparan la asignación por día.
const diaLocal = (m) => moment(m.format("YYYY-MM-DD"), "YYYY-MM-DD");

class PasoRelojChecador extends Component {
  state = {
    respuesta: null, // null sin contestar · true quiere asignar · false no
    mostrandoFormulario: true,
    relojId: null,
    reloj: null,
    pin: null,
    enviando: false,
    asignados: [],
    turnoId: null,
    turnoNombre: "",
    inicioTurno: this.inicioPropuesto(),
    sinFinTurno: true,
    finTurno: null,
    enviandoTurno: false,
    turnoAsignado: null, // { nombre, inicio, fin } — fin null si quedó abierta
    hayTurnos: null, // null sin revisar · false la empresa no tiene turnos activos
  };

  // Lo último que se le avisó al asistente con onTurnoPendiente.
  pendienteAvisado = false;

  componentDidUpdate() {
    const pendiente = this.turnoPendiente();
    if (pendiente !== this.pendienteAvisado) {
      this.pendienteAvisado = pendiente;
      this.props.onTurnoPendiente?.(pendiente);
    }
  }

  componentWillUnmount() {
    if (this.pendienteAvisado) this.props.onTurnoPendiente?.(false);
  }

  inicioPropuesto() {
    const clave = claveFechaDia(this.props.fechaAlta);
    return clave ? moment(clave, "YYYY-MM-DD") : moment().startOf("day");
  }

  turnoPendiente() {
    const { asignados, turnoAsignado, hayTurnos } = this.state;
    return !!this.props.puedeAsignarTurno && asignados.length > 0 && !turnoAsignado && hayTurnos !== false;
  }

  elegirReloj = (relojId, reloj) => {
    // El PIN sugerido es el siguiente libre de ese reloj físico (lo calcula el backend).
    this.setState({ relojId, reloj: reloj || null, pin: reloj?.numeroSiguiente ?? this.state.pin });
  };

  enviar = async () => {
    const { relojId, reloj, pin, asignados } = this.state;
    if (!relojId) return message.info("Elige el reloj checador");
    if (!pin || pin < 1) return message.info("Captura el PIN del empleado en el reloj");
    if (asignados.some((a) => a.relojId === relojId)) return message.info("Ya lo asignaste a ese reloj");

    this.setState({ enviando: true });
    try {
      const res = await axios.post("asignacionesRelojes/agregarAsignacionReloj", {
        empresaId: this.props.empresaId,
        relojChecadorId: relojId,
        empleadoId: this.props.empleadoId,
        usuarioRelojChecador: String(pin),
      }, { headers: this.props.headersToken });

      if (res.data.success === 2) {
        message.success("Empleado enviado al reloj checador");
        this.setState((s) => ({
          asignados: [...s.asignados, { relojId, nombre: reloj?.nombre || reloj?.stringBuscar || "Reloj checador", pin }],
          mostrandoFormulario: false,
          relojId: null,
          reloj: null,
          pin: null,
        }));
        if (this.props.puedeAsignarTurno) this.revisarTurnos();
      } else {
        // success 1: PIN ocupado en ese reloj, reloj o empleado no encontrado.
        message.error(res.data.message || "No se pudo asignar el reloj checador");
      }
    } catch (err) {
      console.log(err);
      message.error("No se pudo asignar el reloj checador");
    } finally {
      this.setState({ enviando: false });
    }
  };

  // ¿La empresa tiene algún turno activo? Sin ninguno no se puede exigir el turno. Si la consulta
  // falla se queda en null y el turno sigue siendo obligatorio.
  revisarTurnos = async () => {
    if (this.state.hayTurnos !== null) return;
    try {
      const res = await axios.post("turnos/selectTurnosItem", {
        empresaId: this.props.empresaId,
        query: "",
      }, { headers: this.props.headersToken });
      if (res.data.success === 2) this.setState({ hayTurnos: (res.data.turnos || []).length > 0 });
    } catch (err) {
      console.log(err);
    }
  };

  asignarTurno = async () => {
    const { turnoId, turnoNombre, inicioTurno, sinFinTurno, finTurno } = this.state;
    if (!turnoId) return message.info("Elige el turno");
    if (!inicioTurno) return message.info("Captura la fecha de inicio del turno");
    if (!sinFinTurno && !finTurno) return message.info("Captura la fecha de fin o marca \"Sin fecha de fin\"");

    const inicio = diaLocal(inicioTurno);
    const fin = (sinFinTurno ? moment(FIN_ABIERTO, "YYYY-MM-DD") : diaLocal(finTurno)).endOf("day");
    if (fin.isBefore(inicio)) return message.info("La fecha de fin no puede ser anterior al inicio");

    this.setState({ enviandoTurno: true });
    try {
      const res = await axios.post("asignacionesTurnos/agregarAsignacionTurno", {
        empresaId: this.props.empresaId,
        empleadoId: this.props.empleadoId,
        turnoId,
        inicio: inicio.toDate(),
        fin: fin.toDate(),
      }, { headers: this.props.headersToken });

      if (res.data.success === 2) {
        message.success("Turno asignado");
        this.setState({ turnoAsignado: { nombre: turnoNombre || "Turno", inicio, fin: sinFinTurno ? null : fin } });
      } else {
        // success 1: ya tiene una asignación que se cruza con esas fechas.
        message.error(res.data.success === 1 && res.data.message ? res.data.message : "No se pudo asignar el turno");
      }
    } catch (err) {
      console.log(err);
      message.error("No se pudo asignar el turno");
    } finally {
      this.setState({ enviandoTurno: false });
    }
  };

  renderOpcion(valor, texto) {
    const elegida = this.state.respuesta === valor;
    return (
      <div
        className={`altapasos-opcion${elegida ? " elegida" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => (valor ? this.setState({ respuesta: true }) : this.props.onTerminar())}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.click(); } }}
      >
        <span className="altapasos-opcion-marca"><CheckOutlined /></span>
        {texto}
      </div>
    );
  }

  renderFormulario() {
    const { relojId, pin, enviando } = this.state;
    return (
      <div className="altapasos-reloj-form">
        <div>
          <div className="altapasos-campo-etiqueta">Reloj checador</div>
          <SelectRelojesChecadoresItem
            empresaId={this.props.empresaId}
            headersToken={this.props.headersToken}
            value={relojId || undefined}
            onChange={this.elegirReloj}
            placeholder="Elige el reloj"
            width="100%"
          />
        </div>
        <div>
          <div className="altapasos-campo-etiqueta">PIN en el reloj</div>
          <InputNumber
            min={1}
            precision={0}
            value={pin}
            onChange={(v) => this.setState({ pin: v })}
            placeholder="PIN"
            style={{ width: "100%" }}
          />
        </div>
        <ButtonItem type="primary" cyan htmlType="button" icon={<FieldTimeOutlined />} disabled={enviando} loading={enviando} onClick={this.enviar}>
          Enviar al reloj
        </ButtonItem>
      </div>
    );
  }

  renderFormularioTurno() {
    const { turnoId, inicioTurno, sinFinTurno, finTurno, enviandoTurno } = this.state;
    return (
      <div className="altapasos-turno-form">
        <div>
          <div className="altapasos-campo-etiqueta">Turno</div>
          <SelectTurnosItem
            empresaId={this.props.empresaId}
            headersToken={this.props.headersToken}
            value={turnoId || undefined}
            onChange={(id, turno) => this.setState({ turnoId: id || null, turnoNombre: turno?.stringBuscar || turno?.nombre || "" })}
            placeholder="Elige el turno"
            width="100%"
          />
        </div>
        <div>
          <div className="altapasos-campo-etiqueta">Inicio</div>
          <DatePickerItem
            value={inicioTurno}
            onChange={(v) => this.setState({ inicioTurno: v })}
            allowClear={false}
            placeholder="Inicio"
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <div className="altapasos-campo-etiqueta">
            Fin
            <Checkbox
              checked={sinFinTurno}
              onChange={(e) => this.setState({ sinFinTurno: e.target.checked })}
              style={{ marginLeft: 8, fontSize: 12, fontWeight: 400 }}
            >
              Sin fecha de fin
            </Checkbox>
          </div>
          <DatePickerItem
            value={sinFinTurno ? null : finTurno}
            onChange={(v) => this.setState({ finTurno: v })}
            disabled={sinFinTurno}
            placeholder={sinFinTurno ? "Sin fin" : "Fin"}
            style={{ width: "100%" }}
          />
        </div>
        <ButtonItem type="primary" cyan htmlType="button" icon={<ScheduleOutlined />} disabled={enviandoTurno} loading={enviandoTurno} onClick={this.asignarTurno}>
          Asignar turno
        </ButtonItem>
      </div>
    );
  }

  renderTurno() {
    const { turnoAsignado, hayTurnos } = this.state;
    return (
      <div className="altapasos-pregunta">
        <div className="altapasos-pregunta-titulo">Asígnale su turno</div>
        <div className="altapasos-pregunta-ayuda">
          Ya checa en un reloj: con el turno se calculan sus retardos, faltas y horas trabajadas. Es necesario para terminar.
        </div>

        {turnoAsignado && (
          <div className="altapasos-reloj-asignado">
            <CheckCircleFilled style={{ color: "#2e9e6b", fontSize: 16 }} />
            <span>
              Turno <b>{turnoAsignado.nombre}</b> desde el <b>{turnoAsignado.inicio.format("DD/MM/YYYY")}</b>
              {turnoAsignado.fin ? <> hasta el <b>{turnoAsignado.fin.format("DD/MM/YYYY")}</b></> : ", sin fecha de fin"}
            </span>
          </div>
        )}
        {!turnoAsignado && hayTurnos === false && (
          <div className="altapasos-panel aviso">
            <div className="altapasos-panel-titulo"><WarningFilled style={{ color: "#faad14" }} /> La empresa no tiene turnos activos</div>
            <div className="altapasos-panel-texto">
              Dalos de alta en Turnos y asígnale el suyo desde Asignación de Turnos. Mientras tanto puedes terminar el alta.
            </div>
          </div>
        )}
        {!turnoAsignado && hayTurnos !== false && this.renderFormularioTurno()}
      </div>
    );
  }

  render() {
    const { nombre, puedeAsignar, puedeAsignarTurno } = this.props;
    const { respuesta, asignados, mostrandoFormulario } = this.state;
    const turnoPendiente = this.turnoPendiente();

    return (
      <>
        <Result
          status="success"
          icon={<CheckCircleFilled style={{ color: "#2e9e6b" }} />}
          title={`¡${nombre || "El empleado"} ya está dado de alta!`}
          subTitle="Ya es un empleado activo y entra a las nóminas nuevas."
          style={{ padding: "8px 0 16px" }}
          extra={!puedeAsignar ? [
            <ButtonItem key="terminar" type="primary" cyan htmlType="button" onClick={this.props.onTerminar}>Ir a Empleados</ButtonItem>,
          ] : undefined}
        />

        {puedeAsignar && (
          <div className="altapasos-pregunta">
            <div className="altapasos-pregunta-titulo">¿Quieres darlo de alta en un reloj checador?</div>
            <div className="altapasos-pregunta-ayuda">
              Se le asigna un PIN en el reloj para que pueda checar su entrada y salida.
            </div>

            {!asignados.length && (
              <div className="altapasos-opciones">
                {this.renderOpcion(true, "Sí, asignar a un reloj")}
                {this.renderOpcion(false, "No, terminar")}
              </div>
            )}

            {asignados.map((a) => (
              <div key={a.relojId} className="altapasos-reloj-asignado">
                <CheckCircleFilled style={{ color: "#2e9e6b", fontSize: 16 }} />
                <span>Asignado a <b>{a.nombre}</b> con el PIN <b>{a.pin}</b></span>
              </div>
            ))}
            {asignados.length > 0 && (
              <div className="altapasos-panel aviso" style={{ marginTop: 10 }}>
                <div className="altapasos-panel-titulo"><InfoCircleFilled style={{ color: "#faad14" }} /> Lo que sigue</div>
                <div className="altapasos-panel-texto">
                  Cuando el reloj se conecte recibirá al empleado. Registra su huella o rostro
                  directamente en el reloj con el PIN que se le asignó.
                </div>
              </div>
            )}

            {respuesta === true && mostrandoFormulario && this.renderFormulario()}

            {asignados.length > 0 && !mostrandoFormulario && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                <ButtonItem blanco htmlType="button" icon={<FieldTimeOutlined />} onClick={() => this.setState({ mostrandoFormulario: true })}>
                  Asignar a otro reloj
                </ButtonItem>
              </div>
            )}
          </div>
        )}

        {puedeAsignar && puedeAsignarTurno && asignados.length > 0 && this.renderTurno()}

        {puedeAsignar && asignados.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10 }}>
            {turnoPendiente && <span style={{ fontSize: 12, color: "#7a8794" }}>Falta asignarle su turno</span>}
            <ButtonItem type="primary" cyan htmlType="button" disabled={turnoPendiente} onClick={this.props.onTerminar}>
              Terminar
            </ButtonItem>
          </div>
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(PasoRelojChecador);
